import React, { useState } from 'react'
import { Phone, Mail, MapPin, ShieldCheck, Send, CheckCircle2, AlertCircle } from 'lucide-react'

const LOGO_URL = "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/6fc95741-bbf3-49e6-9c69-766b0204a25f.png"

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot felt
  })

  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('')

  React.useEffect(() => {
    // Sæt meta-data
    document.title = "Kontakt os | D&W Udlejning ApS"
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Kontakt D&W Udlejning ApS i Skibby for leje af mindre entreprenørmaskiner. Ring til os på 20 82 89 70 eller send en besked her.')
    }
  }, [])

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrævet'
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast venligst et gyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast venligst en gyldig e-mailadresse'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse er påkrævet'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Fjern fejlbesked, når brugeren skriver
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus('submitting')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        })
      } else {
        throw new Error('Der skete en fejl ved afsendelse af formularen.')
      }
    } catch (err) {
      console.error('Formular indsendelsesfejl:', err)
      setStatus('error')
      setStatusMessage('Kunne ikke sende din besked lige nu. Prøv igen senere eller ring til os direkte.')
    }
  }

  return (
    <div className="bg-neutral-50 text-neutral-900 min-h-screen selection:bg-amber-500 selection:text-neutral-950">
      {/* INTRODUKTION SEKTION */}
      <section className="relative py-24 bg-white border-b border-neutral-200 overflow-hidden">
        {/* Subtilt geometrisk grid-mønster og logo-vandmærke */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none opacity-60" />
        <div className="absolute right-[-5%] top-[-5%] w-[400px] h-[400px] pointer-events-none opacity-[0.03] select-none">
          <img src={LOGO_URL} alt="D&W Logo Vandmærke" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
            Kontakt os
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-neutral-900">
            Kontakt <span className="text-amber-600">D&W Udlejning ApS</span>
          </h1>
          <p className="text-neutral-600 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Har du spørgsmål til vores maskiner, priser eller ledighed? Vi sidder klar til at hjælpe dig med at finde den helt rigtige løsning til dit projekt. Ring, skriv en mail eller udfyld formularen herunder.
          </p>
        </div>
      </section>

      {/* KONTAKT INFO & FORMULAR */}
      <section className="relative py-24 overflow-hidden">
        {/* Subtilt geometrisk grid-mønster */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none opacity-40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Venstre side: Kontaktoplysninger */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
                  Kontaktoplysninger
                </span>
                <h3 className="text-2xl sm:text-3xl font-black text-neutral-900 tracking-tight">
                  Vi er altid klar til at hjælpe dig
                </h3>
                <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
                  Du er altid velkommen til at kontakte os. Vi bestræber os på at besvare alle skriftlige henvendelser inden for få timer.
                </p>
              </div>

              <div className="space-y-6">
                {/* Adresse */}
                <div className="flex items-start gap-4 bg-white border border-neutral-200 p-5 rounded-xl shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">Adresse</h4>
                    <p className="text-base font-bold text-neutral-800 mt-1">D&W Udlejning ApS</p>
                    <p className="text-neutral-600 text-sm">Nyvej 9</p>
                    <p className="text-neutral-600 text-sm">4050 Skibby</p>
                  </div>
                </div>

                {/* Telefon */}
                <div className="flex items-start gap-4 bg-white border border-neutral-200 p-5 rounded-xl shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">Telefon</h4>
                    <a
                      href="tel:20828970"
                      className="block text-lg font-black text-neutral-900 hover:text-amber-600 transition-colors duration-200 mt-1 whitespace-nowrap"
                    >
                      20 82 89 70
                    </a>
                    <p className="text-neutral-400 text-xs mt-1">Ring alle ugens dage for hurtigt svar</p>
                  </div>
                </div>

                {/* E-mail */}
                <div className="flex items-start gap-4 bg-white border border-neutral-200 p-5 rounded-xl shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">E-mail</h4>
                    <a
                      href="mailto:dw@dw-udlejning.dk"
                      className="block text-base font-bold text-neutral-900 hover:text-amber-600 transition-colors duration-200 mt-1 whitespace-nowrap"
                    >
                      dw@dw-udlejning.dk
                    </a>
                  </div>
                </div>
              </div>

              {/* CVR og Sikkerhed */}
              <div className="pt-4 border-t border-neutral-200 flex items-center justify-between text-xs text-neutral-400 font-bold">
                <span>CVR-nr: 46378458</span>
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4 text-amber-500" />
                  Dansk virksomhed
                </span>
              </div>
            </div>

            {/* Højre side: Kontaktformular */}
            <div className="lg:col-span-7 bg-white border border-neutral-200 p-6 sm:p-10 rounded-2xl shadow-sm">
              <div className="mb-8">
                <h3 className="text-xl font-bold text-neutral-900 tracking-tight">Send os en besked</h3>
                <p className="text-neutral-500 text-sm mt-1">Udfyld formularen, så vender vi hurtigt tilbage til dig.</p>
              </div>

              {status === 'success' ? (
                <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900">Tak for din henvendelse!</h4>
                  <p className="text-neutral-600 text-sm max-w-md mx-auto leading-relaxed">
                    Vi har modtaget din besked og vil vende tilbage til dig så hurtigt som muligt – normalt inden for få timer.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-2 text-xs font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider focus:outline-none cursor-pointer"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot felt - absolut skjult for mennesker */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <label htmlFor="website_url">Honeypot</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Navn */}
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-xs font-bold text-neutral-500 uppercase tracking-wider">
                      Navn <span className="text-amber-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full bg-neutral-50 border ${
                        errors.name ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:border-amber-500'
                      } rounded-md px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2`}
                      placeholder="Dit fulde navn"
                    />
                    {errors.name && <p className="text-red-500 text-xs font-semibold mt-1">{errors.name}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Telefonnummer */}
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-xs font-bold text-neutral-500 uppercase tracking-wider">
                        Telefonnummer <span className="text-amber-500">*</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full bg-neutral-50 border ${
                          errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:border-amber-500'
                        } rounded-md px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2`}
                        placeholder="F.eks. 20828970"
                      />
                      {errors.phone && <p className="text-red-500 text-xs font-semibold mt-1">{errors.phone}</p>}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-xs font-bold text-neutral-500 uppercase tracking-wider">
                        E-mail <span className="text-amber-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full bg-neutral-50 border ${
                          errors.email ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:border-amber-500'
                        } rounded-md px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2`}
                        placeholder="din@email.dk"
                      />
                      {errors.email && <p className="text-red-500 text-xs font-semibold mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  {/* Beskrivelse */}
                  <div className="space-y-2">
                    <label htmlFor="description" className="block text-xs font-bold text-neutral-500 uppercase tracking-wider">
                      Beskrivelse / Forespørgsel <span className="text-amber-500">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows="5"
                      value={formData.description}
                      onChange={handleChange}
                      className={`w-full bg-neutral-50 border ${
                        errors.description ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:border-amber-500'
                      } rounded-md px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 resize-none`}
                      placeholder="Skriv hvilken maskine du ønsker at leje, samt hvornår og hvor længe..."
                    />
                    {errors.description && <p className="text-red-500 text-xs font-semibold mt-1">{errors.description}</p>}
                  </div>

                  {/* Fejlmeddelelse fra API */}
                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-md flex items-start gap-3 text-red-600 text-sm">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                      <span>{statusMessage}</span>
                    </div>
                  )}

                  {/* Indsend-knap */}
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-amber-500 hover:bg-amber-400 disabled:bg-neutral-200 text-neutral-950 disabled:text-neutral-400 font-bold text-base transition-colors duration-200 cursor-pointer shadow-md shadow-amber-500/10"
                  >
                    {status === 'submitting' ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-neutral-950" />
                        <span>Sender besked...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send forespørgsel</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
